import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import {
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';

import { loginUserSuccess, logoutUserSuccess } from 'ish-core/store/customer/user/user.actions';
import { HttpStatusCodeService } from 'ish-core/utils/http-status-code/http-status-code.service';
import { mapErrorToAction, mapToPayload, mapToPayloadProperty } from 'ish-core/utils/operators';

import { DotCmsFacade } from '../../facades/dot-cms.facade';
import { CustomDotCmsPageMapper } from '../../models/page/custom-dot-cms-page.mapper';
import { DotCmsResourceService } from '../../services/dot-cms-resource/dot-cms-resource.service';
import { dotCMSNavigationLink } from '../../utils/dot-cms-utils';
import { loadDotCmsContainersSuccess } from '../dotcms-container/dotcms-container-action';
import { loadDotCmsContentletsSuccess } from '../dotcms-contentlets/dotcms-contentlets.action';
import { getDotCmsCommonRequestParams } from '../utils/dotcms-store-utils';

import {
  loadDotCmsPage,
  loadDotCmsPageFail,
  loadDotCmsPageSuccess,
  loadUVEDotCmsPageData,
  resetDotCmsPageData,
} from './dotcms-pages.action';

@Injectable()
// eslint-disable-next-line ish-custom-rules/project-structure
export class DotCmsPagesEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private dotCmsResourceService: DotCmsResourceService,
    private httpStatusCodeService: HttpStatusCodeService,
    private dotCmsFacade: DotCmsFacade
  ) {}

  loadDotCmsPages$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(loadDotCmsPage)), this.dotCmsFacade.setDotCMSClientEnvironment$()]).pipe(
      takeWhile(([_, allowed]) => this.router.navigated && allowed),
      map(([action]) => action),
      debounceTime(100),
      mapToPayload(),
      getDotCmsCommonRequestParams(this.store),
      mergeMap(([{ pageURI, params }, hostId, language]) =>
        this.dotCmsResourceService
          .getPageByURI(pageURI, {
            hostId,
            language: params.language || language,
            depth: params.depth,
            personaId: params.personaId,
            variantName: params.variantName,
            mode: params.mode,
          })
          .pipe(
            tap(
              ([page]) =>
                page?.metaInfo?.redirect && this.router.navigate([dotCMSNavigationLink(page.metaInfo.redirect)])
            ),
            filter(([page]) => !page?.metaInfo?.redirect), // Filter out actions if there's a redirect
            switchMap(([page, contentlets, containers]) => [
              loadDotCmsPageSuccess({ page }),
              loadDotCmsContentletsSuccess({ contentlets }),
              loadDotCmsContainersSuccess({ containers }),
            ]),
            mapErrorToAction(loadDotCmsPageFail)
          )
      )
    )
  );

  //Todo: apply configuration needs to be dispatched within switchMap
  loadDotCmsEmaPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUVEDotCmsPageData),
      mapToPayloadProperty('page'),
      distinctUntilChanged(isEqual),
      map(payload => CustomDotCmsPageMapper.fromData(payload)),
      switchMap(([page, contentlets, containers]) => [
        loadDotCmsPageSuccess({ page }),
        loadDotCmsContentletsSuccess({ contentlets }),
        loadDotCmsContainersSuccess({ containers }),
      ])
    )
  );

  resetDotCmsPageData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginUserSuccess, logoutUserSuccess),
      map(() => resetDotCmsPageData())
    )
  );

  redirectIfErrorInDotCmsPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadDotCmsPageFail),
        concatMap(() => from(this.httpStatusCodeService.setStatus(404)))
      ),
    { dispatch: false }
  );
}
